<template>
  <page-header title="Plugin" :identifier="pluginId">
    <template v-if="isLoaded">
      <button v-if="pluginId" class="btn-outline-primary ml-auto h-10 mr-2" @click="toggleEnableDisablePlugin">
        <template v-if="plugin.isEnabled">
          <LockClosedIcon class="size-5 -ml-1 mr-2" />
          Disable
        </template>
        <template v-else>
          <LockOpenIcon class="size-5 -ml-1 mr-2" />
          Enable
        </template>
      </button>
      <button class="btn-primary ml-auto h-10" :class="{'disabled': isSaving || !isDataChanged}" @click="save">
        <CircleStackIcon class="size-5 -ml-1 mr-2" />
        Save
      </button>
    </template>
  </page-header>

  <plugin-form v-if="isLoaded" ref="pluginFormRef" :plugin="plugin" @save="save" />

  <div v-else>
    Loading...
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, toRaw } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import PageHeader from '../components/PageHeader.vue';
import PluginForm from '../components/PluginForm.vue';
import {
  CircleStackIcon,
  LockOpenIcon,
  LockClosedIcon
} from '@heroicons/vue/24/outline';
import { useFetch } from '@vueuse/core';
import { apiDomain } from '../composables/useConstants';
import { Modal } from '../composables/useModal';
import { useToast } from 'vue-toastification';
import hashSum from 'hash-sum';

const props = defineProps({
  pluginId: {
    type: String,
    default: null
  }
});

const toast = useToast();
const pluginFormRef = ref(null);
const isLoaded = ref(false);
const isSaving = ref(false);
const plugin = ref({});
const pluginHashSum = ref(null);

const isDataChanged = computed(() => pluginHashSum.value !== hashSum(plugin.value));

if (props.pluginId) {
  load();
} else {
  isLoaded.value = true;
}

async function load() {
  const { data } = await useFetch(`${apiDomain}/api/plugin/${props.pluginId}`).get().json();
  pluginHashSum.value = hashSum(data.value);
  plugin.value = data.value;
  isLoaded.value = true;
}

async function save() {
  isSaving.value = true;

  pluginFormRef.value.onValidate();
  if (pluginFormRef.value.getResult().$invalid) {
    return;
  }

  const { data, error } = await useFetch(`${apiDomain}/api/plugin/`).post(toRaw(plugin.value)).json();

  isSaving.value = false;

  if (error.value) {
    toast.error(error.value);
  }

  if (data.value) {
    pluginHashSum.value = hashSum(data.value);
    plugin.value = data.value;
    toast.success('Plugin saved successfully');
  }
}

async function toggleEnableDisablePlugin() {
  const direction = plugin.value.isEnabled ? 'disable' : 'enable';
  const { data } = await useFetch(`${apiDomain}/api/plugin/${direction}/${plugin.value.id}`).get().json();

  if (data.value?.success) {
    load();
    toast.success(`Plugin ${direction}d successfully`);
    return;
  }

  toast.error('Error enabling plugin');
}

function handleBeforeUnload(e) {
  if (!isDataChanged.value || !Object.keys(plugin.value).length) {
    return;
  }
  e.preventDefault();
  e.returnValue = '';
}

onBeforeRouteLeave(async (to, from, next) => {
  if (!isDataChanged.value || !Object.keys(plugin.value).length) {
    return next();
  }

  const confirm = await Modal.confirm({
    title: 'Unsaved changes',
    body: 'You have unsaved changes. <br/> If you leave this page, your changes will be lost. <br/> Are you sure you want to exit without saving?',
    okButton: 'Leave',
    iconColor: 'primary'
  });

  return confirm ? next() : next(false);
});

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>
